import { computed, inject, Injectable, signal } from '@angular/core';
import { RolesEnum } from '../../enums/roles-enum';
import { SessionUser } from './session-user.interface';
import { v4 as uuidv4 } from 'uuid';
import { StorageService } from '../storage/storage.service';

const DEFAULT_TIME_IN_MINUTES_TO_REFRESH_SESSION_ID = 5;

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private storage = inject(StorageService);
  organization: any;
  intermediary: any;
  isTrial: boolean;
  role: any;

  private user = signal<SessionUser | null>(null);
  isAuthenticated = computed(() => !!this.user);

  get currentUser() {
    return this.user();
  }

  set currentUser(user: SessionUser | null) {
    this.user.set(user);
  }

  get roles() {
    return {
      [RolesEnum.ADMINISTRATOR]: '/enter-as',
      [RolesEnum.NETWORK_ADMINISTRATOR]: '/network',
      [RolesEnum.ACCOUNT_ADMINISTRATORS]: 'surveys',
      [RolesEnum.PROGRAM_ADMINISTRATOR]: '/surveys',
      [RolesEnum.REPORT_VIEWER]: '/reports',
    };
  }

  constructor() {
    const user = this.storage.getUser();

    if (user) {
      this.currentUser = user;
    }
  }

  getToken = () => this.storage.getToken();

  setToken = (token) => this.storage.setToken(token);

  generateToken = (email, password) =>
    'Basic ' + btoa(unescape(encodeURIComponent(email + ':' + password)));

  entered_as = () => !!this.storage.getAdminSession();

  getUser = () => this.storage.getUser();

  getAdminUser = () => {
    const adminSession = this.storage.getAdminSession();
    return adminSession?.user || this.getUser();
  };

  getOrganization = () => this.storage.getOrganization();

  getProgram = () => this.storage.getProgramId();

  isOrganizationLeader = () =>
    this.storage.getRole() == RolesEnum.ACCOUNT_ADMINISTRATORS;

  isProgramLeader = () =>
    this.storage.getRole() == RolesEnum.PROGRAM_ADMINISTRATOR;

  isIntermediaryUser = () =>
    this.storage.getRole() == RolesEnum.NETWORK_ADMINISTRATOR;

  isAdministrator = () => this.storage.getRole() == RolesEnum.ADMINISTRATOR;

  isReportViewer = () => this.storage.getRole() === RolesEnum.REPORT_VIEWER;

  setUser = (user) => this.storage.setUser(user);

  generateSessionId = () => this.storage.setSessionId(uuidv4());

  saveLastEventTime = () =>
    this.storage.setLastEventTime(new Date().toString());

  setOrganization(org) {
    if (org === undefined || org === null) {
      return;
    }
    this.organization = org;
    if (org.account_type && org.account_type === 'free') {
      this.isTrial = true;
    }
    if (org.account_type && org.account_type === 'paid') {
      this.isTrial = false;
    }
    this.storage.setOrganization(org);
  }

  getSessionId = () => this.storage.getSessionId();

  getLastEventTime = () => this.storage.getLastEventTime();

  getIntermediary = () => this.storage.getIntermediary();

  setIntermediary(im) {
    if (!im) return;
    this.intermediary = im;
    this.storage.setIntermediary(im);
  }

  setRole(role) {
    this.role = role;
    this.storage.setRole(role);
  }

  getRole() {
    return this.storage.getRole();
  }

  loggedIn() {
    return !!(this.storage.getToken() && !!this.storage.getUser());
  }

  getSubscriptions() {
    return this.storage.getSubscriptions();
  }

  enter_as(user) {
    const _admin_session = {
      user: this.storage.getUser(),
      role: this.storage.getRole(),
    };
    this.storage.setAdminSession(_admin_session);
    this.setUser(user);
  }

  end() {
    //self.user = undefined;
    this.role = undefined;
    this.organization = undefined;
    //self._admin_session = undefined;
    this.storage.clearSession();
  }

  restore_admin_session() {
    if (this.entered_as()) {
      const adminSession = this.storage.getAdminSession();
      this.setUser(adminSession.user);
      this.setRole(adminSession.role);
      this.storage.removeAdminSession();
      this.organization = undefined;
      this.storage.removeOrganization();
    } else {
      throw 'Admin session was not found';
    }
  }

  setProgramIDUrl(id: string): void {
    this.storage.setProgramIdUrl(id);
  }

  getProgramIDUrl(): string {
    return this.storage.getProgramIdUrl();
  }

  removeProgramIDUrl(): void {
    this.storage.removeProgramIdUrl();
  }

  checkIfNeedNewSessionId(): void {
    const lastEventTime = this.getLastEventTime();

    if (!lastEventTime) {
      return;
    }

    const lastEventDate: Date = new Date(lastEventTime);
    const currentTime: Date = new Date();

    const timeDifferenceInMinutes =
      (currentTime.getTime() - lastEventDate.getTime()) / 1000 / 60;

    if (
      timeDifferenceInMinutes > DEFAULT_TIME_IN_MINUTES_TO_REFRESH_SESSION_ID
    ) {
      this.generateSessionId();
    }
  }
}
